import { appTimeZone, zoneParts } from '@/shared/lib'

// Период отчёта — пара календарных дней «YYYY-MM-DD» включительно, в зоне
// организации (APP_TZ). Тот же формат у <input type="date"> и у query-параметров
// ручки, а бэкенд превращает эти дни в границы суток той же зоны — поэтому день
// нигде не пересчитывается через момент и не съезжает на офсет.
export interface ReportRange {
  from: string
  to: string
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export function isReportDate(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_PATTERN.test(value)) return false
  const d = civil(value)
  // Обратное форматирование ловит переполнение: Date.UTC молча превратит
  // «2026-13-40» в существующий день будущего года.
  return !Number.isNaN(d.getTime()) && toReportDate(d) === value
}

// Календарный день как Date в UTC-полночь. Это НЕ момент периода, а носитель
// пары «год-месяц-число» для арифметики: UTC здесь техническая зона, из которой
// читают только getUTC*, поэтому зона устройства в счёт не идёт.
function civil(date: string): Date {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1))
}

function toReportDate(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${date.getUTCFullYear()}-${month}-${day}`
}

// «Сегодня» — день, на который приходится момент now в зоне организации.
export function today(now: Date = new Date(), tz: string = appTimeZone()): string {
  const p = zoneParts(now, tz)
  return toReportDate(new Date(Date.UTC(p.year, p.month - 1, p.day)))
}

// offset: 0 — текущий месяц, -1 — прошлый. Месяц календарный: конец текущего не
// обрезаем сегодняшним днём.
export function monthRange(
  offset = 0,
  now: Date = new Date(),
  tz: string = appTimeZone(),
): ReportRange {
  const p = zoneParts(now, tz)
  const start = new Date(Date.UTC(p.year, p.month - 1 + offset, 1))
  // Нулевое число следующего месяца — последний день нужного.
  const end = new Date(Date.UTC(p.year, p.month + offset, 0))
  return { from: toReportDate(start), to: toReportDate(end) }
}

export function currentMonthRange(now: Date = new Date()): ReportRange {
  return monthRange(0, now)
}

// Последние N дней, считая сегодняшний (по календарю организации).
export function lastDaysRange(
  days: number,
  now: Date = new Date(),
  tz: string = appTimeZone(),
): ReportRange {
  const to = today(now, tz)
  const start = civil(to)
  start.setUTCDate(start.getUTCDate() - (days - 1))
  return { from: toReportDate(start), to }
}

// Перевёрнутый период (конец раньше начала) — не ошибка ввода, а порядок дат:
// разворачиваем молча, ругаться на человека здесь не за что.
export function normalizeRange(range: ReportRange): ReportRange {
  return range.from > range.to ? { from: range.to, to: range.from } : range
}

export function sameRange(a: ReportRange, b: ReportRange): boolean {
  return a.from === b.from && a.to === b.to
}

// Принимает и день периода («2026-08-01»), и полный ISO-момент из ответа — в
// обоих случаях показываем день: момент читается в зоне организации, день
// печатается как есть.
export function formatReportDate(date: string, tz: string = appTimeZone()): string {
  if (DATE_PATTERN.test(date)) return formatCivil(civil(date))
  const at = new Date(date)
  if (Number.isNaN(at.getTime())) return date
  const p = zoneParts(at, tz)
  return formatCivil(new Date(Date.UTC(p.year, p.month - 1, p.day)))
}

// Календарный день печатается в UTC — той же технической зоне, в которой он
// собран: подставить сюда зону показа значило бы сдвинуть дату на сутки.
function formatCivil(date: Date): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

// «1 — 31 августа 2026» внутри месяца, иначе обе даты целиком.
export function formatRangeLabel(range: ReportRange): string {
  if (!isReportDate(range.from) || !isReportDate(range.to)) {
    return `${range.from} — ${range.to}`
  }
  const from = civil(range.from)
  const to = civil(range.to)
  if (from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth()) {
    return `${from.getUTCDate()} — ${formatReportDate(range.to)}`
  }
  return `${formatReportDate(range.from)} — ${formatReportDate(range.to)}`
}
